import React, { useState, useEffect, useRef } from "react";
import styles from "./operators-modal.module.css";
import Input from "../../ui/input";
import { MyIcons } from "../../icons/my-icons";
import Dropdown from "../../ui/dropdown";
import Button from "../../ui/button";
import Table from "../../ui/table";
import Checkbox from "../../ui/checkbox";
import ContactItem from "../../screens/contact-item";
import EditContactModal from "../edit-contact-modal";
import FormSection from "../../screens/form-section";
import { getWorkers, getGuilds, getLocalities, createOperator, getOperatorById, editOperator, searchOperatorsByName } from "../../../services/apiService";
import { showErrorToast } from "../../../utils/notifications";

const OperatorsModal = ({ isOpen, onClose, initialData }) => {
  const modalRef = useRef(null);
  const firstInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    locality: null, // Cambiado para manejar el objeto localidad
    phone: initialData?.phoneNumber || "",
    email: "",
    dni: "",
    observations: "",
    guilds: [], // Array para multiselect
    contacts: [], // Array para contactos
  });
  const [operators, setOperators] = useState([]);
  const [guildOptions, setGuildOptions] = useState([]);
  const [localityOptions, setLocalityOptions] = useState([]);
  const [isInactive, setIsInactive] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingOperatorId, setEditingOperatorId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Estados para contactos
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState(initialData?.phoneNumber || "");
  const [isEditContactModalOpen, setIsEditContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  // Validaciones
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Verificar si el formulario es válido
  const isFormValid = () => {
    return (
      formData.name.trim() !== "" &&
      formData.address.trim() !== "" &&
      formData.locality !== null &&
      formData.email.trim() !== "" &&
      validateEmail(formData.email) &&
      formData.dni.trim() !== "" &&
      formData.guilds.length > 0
    );
  };

  useEffect(() => {
    // Actualizar teléfonos si se proporciona initialData
    if (initialData?.phoneNumber) {
      setFormData(prev => ({
        ...prev,
        phone: initialData.phoneNumber
      }));
      setContactPhone(initialData.phoneNumber);
    }
  }, [initialData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch operators
        const operatorsData = await getWorkers();
        
        // Formatear los datos para la tabla
        const formattedOperators = operatorsData.map(operator => ({
          ...operator,
          localidad: operator.localidad || "Sin localidad",
          gremios: Array.isArray(operator.gremios) 
            ? operator.gremios.join(", ") 
            : operator.gremios || "Sin gremios",
          estado: operator.inactivo ? "Inactivo" : "Activo"
        }));
        
        setOperators(formattedOperators);

        // Fetch guilds
        const guildsData = await getGuilds();
        // Transformar la respuesta de la API al formato del dropdown
        const formattedGuilds = guildsData.map(guild => ({
          label: guild.nombre, // Asumiendo que la API devuelve 'nombre'
          value: guild.id      // Asumiendo que la API devuelve 'id'
        }));
        setGuildOptions(formattedGuilds);

        // Fetch localities
        const localitiesData = await getLocalities();
        const formattedLocalities = localitiesData.map(locality => ({
          label: `${locality.municipio} (${locality.codigoPostal})`,
          value: locality.id
        }));
        setLocalityOptions(formattedLocalities);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // No cerrar si el EditContactModal está abierto
      if (isEditContactModalOpen) {
        return;
      }
      
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose, isEditContactModalOpen]);

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleGuildsSelect = (selectedGuilds) => {
    setFormData({ ...formData, guilds: selectedGuilds });
  };

  const handleLocalitySelect = (selectedLocality) => {
    setFormData({ ...formData, locality: selectedLocality });
  };

  const handleSaveOperator = async () => {
    try {
      // Validar email
      if (!validateEmail(formData.email)) {
        showErrorToast("Por favor, ingresa un correo electrónico válido");
        return;
      }

      // Preparar los datos según el formato esperado por la API
      const operatorData = {
        nombre: formData.name,
        domicilio: formData.address,
        localidadId: formData.locality?.value, // ID de la localidad seleccionada
        correo: formData.email,
        dni: formData.dni,
        activo: !isInactive,
        gremiosIds: formData.guilds.map(guild => guild.value), // Array de IDs de gremios
        observaciones: formData.observations,
        contactos: formData.contacts.map(c => ({
          nombre: c.nombre,
          telefono: c.telefono
        }))
      };

      console.log("Datos enviados al crear operario:", operatorData);

      await createOperator(operatorData);
      
      // Refrescar la lista de operarios
      const operatorsData = await getWorkers();
      
      // Formatear los datos para la tabla
      const formattedOperators = operatorsData.map(operator => ({
        ...operator,
        gremios: Array.isArray(operator.gremios) 
          ? operator.gremios.join(", ") 
          : operator.gremios || "Sin gremios",
        estado: operator.inactivo ? "Inactivo" : "Activo"
      }));
      
      setOperators(formattedOperators);
      
      // Limpiar el formulario
      setFormData({
        name: "",
        address: "",
        locality: null,
        phone: "",
        email: "",
        dni: "",
        observations: "",
        guilds: [],
        contacts: [],
      });
      setContactName("");
      setContactPhone("");
      setIsInactive(false);
      
    } catch (error) {
      console.error("Error al crear operario:", error);
    }
  };

  // Manejar edición de operario
  const handleEditOperator = async () => {
    try {
      // Validar email
      if (!validateEmail(formData.email)) {
        alert("Por favor, ingresa un correo electrónico válido");
        return;
      }

      // Preparar los datos según el formato esperado por la API
      const operatorData = {
        nombre: formData.name,
        domicilio: formData.address,
        localidadId: formData.locality?.value,
        correo: formData.email,
        dni: formData.dni,
        activo: !isInactive,
        gremiosIds: formData.guilds.map(guild => guild.value),
        observaciones: formData.observations,
        contactos: formData.contacts.map(c => ({
          nombre: c.nombre,
          telefono: c.telefono
        }))
      };

      console.log("Datos enviados al editar operario:", operatorData);

      await editOperator(editingOperatorId, operatorData);
      
      // Refrescar la lista de operarios
      const operatorsData = await getWorkers();
      
      // Formatear los datos para la tabla
      const formattedOperators = operatorsData.map(operator => ({
        ...operator,
        gremios: Array.isArray(operator.gremios) 
          ? operator.gremios.join(", ") 
          : operator.gremios || "Sin gremios",
        estado: operator.inactivo ? "Inactivo" : "Activo"
      }));
      
      setOperators(formattedOperators);
      
      // Limpiar el formulario y salir del modo edición
      setFormData({
        name: "",
        address: "",
        locality: null,
        phone: "",
        email: "",
        dni: "",
        observations: "",
        guilds: [],
        contacts: [],
      });
      setContactName("");
      setContactPhone("");
      setIsInactive(false);
      setIsEditMode(false);
      setEditingOperatorId(null);
      

    } catch (error) {
      console.error("Error al editar operario:", error);
      showErrorToast("Error al editar el operario. Por favor, intenta de nuevo.");
    }
  };

  // Manejar click en fila de la tabla
  const handleOperatorRowClick = async (operator) => {
    try {
      // Usar el ID correcto según la fuente del operario
      const operatorId = operator.operarioId || operator.id;
      
      // Obtener detalles completos del operario
      const operatorDetails = await getOperatorById(operatorId);
      
      console.log("Datos del operario desde API:", operatorDetails);
      
      // Formatear localidad: la API devuelve localidadId
      let localityFormatted = null;
      if (operatorDetails.localidadId && localityOptions.length > 0) {
        localityFormatted = localityOptions.find(opt => opt.value === operatorDetails.localidadId) || null;
      }
      
      // Formatear gremios: la API devuelve gremiosIds
      let guildsFormatted = [];
      if (operatorDetails.gremiosIds && operatorDetails.gremiosIds.length > 0 && guildOptions.length > 0) {
        guildsFormatted = guildOptions.filter(opt => operatorDetails.gremiosIds.includes(opt.value));
      }
      
      console.log("Localidad formateada:", localityFormatted);
      console.log("Gremios formateados:", guildsFormatted);
      
      // Extraer teléfono del primer contacto si existe
      const phoneNumber = operatorDetails.contactos && operatorDetails.contactos.length > 0
        ? operatorDetails.contactos[0].telefono
        : "";
      
      // Rellenar el formulario
      setFormData({
        name: operatorDetails.nombre || "",
        address: operatorDetails.domicilio || "",
        locality: localityFormatted,
        phone: phoneNumber,
        email: operatorDetails.correo || "",
        dni: operatorDetails.dni || "",
        observations: operatorDetails.observaciones || "",
        guilds: guildsFormatted,
        contacts: operatorDetails.contactos || [],
      });
      
      // Limpiar inputs de contacto
      setContactName("");
      setContactPhone("");
      
      setIsInactive(!operatorDetails.activo);
      setIsEditMode(true);
      setEditingOperatorId(operatorId);
      
      // Hacer scroll al inicio del modal y focus en el primer input
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.scrollTop = 0;
        }
        if (firstInputRef.current) {
          firstInputRef.current.focus();
        }
      }, 100);
      
    } catch (error) {
      console.error("Error al cargar detalles del operario:", error);
        showErrorToast("Error al cargar los datos del operario");
    }
  };

  // Cancelar edición
  const handleCancelEdit = () => {
    setFormData({
      name: "",
      address: "",
      locality: null,
      phone: "",
      email: "",
      dni: "",
      observations: "",
      guilds: [],
      contacts: [],
    });
    setIsInactive(false);
    setIsEditMode(false);
    setEditingOperatorId(null);
    setContactName("");
    setContactPhone("");
  };

  // Búsqueda de operarios
  const handleSearchOperators = async (value) => {
    setSearchTerm(value);
    
    if (!value || value.trim() === "") {
      // Si no hay término de búsqueda, cargar todos los operarios
      try {
        const operatorsData = await getWorkers();
        const formattedOperators = operatorsData.map(operator => ({
          ...operator,
          localidad: operator.localidad || "Sin localidad",
          gremios: Array.isArray(operator.gremios) 
            ? operator.gremios.join(", ") 
            : operator.gremios || "Sin gremios",
          estado: operator.inactivo ? "Inactivo" : "Activo"
        }));
        setOperators(formattedOperators);
      } catch (error) {
        console.error("Error fetching operators:", error);
      }
      return;
    }

    // Buscar operarios por nombre
    try {
      const searchResults = await searchOperatorsByName(value);
      const formattedOperators = searchResults.map(operator => {
        // Formatear localidad desde localidadId
        let localidadNombre = "Sin localidad";
        if (operator.localidadId && localityOptions.length > 0) {
          const localityFound = localityOptions.find(opt => opt.value === operator.localidadId);
          if (localityFound) {
            localidadNombre = localityFound.label;
          }
        }
        
        // Formatear gremios desde gremiosIds
        let gremiosNombres = "Sin gremios";
        if (operator.gremiosIds && Array.isArray(operator.gremiosIds) && operator.gremiosIds.length > 0 && guildOptions.length > 0) {
          const guildsFound = guildOptions.filter(opt => operator.gremiosIds.includes(opt.value));
          if (guildsFound.length > 0) {
            gremiosNombres = guildsFound.map(g => g.label).join(", ");
          }
        }
        
        return {
          ...operator,
          localidad: localidadNombre,
          gremios: gremiosNombres,
          estado: operator.activo === false ? "Inactivo" : "Activo"
        };
      });
      setOperators(formattedOperators);
    } catch (error) {
      console.error("Error searching operators:", error);
    }
  };

  // Funciones para manejar contactos
  const handleAddContact = () => {
    if (!contactName.trim() || !contactPhone.trim()) {
      showErrorToast("Por favor, ingresa nombre y teléfono del contacto");
      return;
    }

    setFormData({
      ...formData,
      contacts: [...formData.contacts, { nombre: contactName, telefono: contactPhone }],
    });

    setContactName("");
    setContactPhone("");
  };

  const handleRemoveContact = (contactId) => {
    setFormData({
      ...formData,
      contacts: formData.contacts.filter((c, index) => index !== contactId),
    });
  };

  const handleEditContact = (contact, index) => {
    setEditingContact({ ...contact, index });
    setIsEditContactModalOpen(true);
  };

  const handleSaveContact = (updatedContact) => {
    const updatedContacts = formData.contacts.map((c, index) =>
      index === updatedContact.index
        ? { nombre: updatedContact.name, telefono: updatedContact.phone }
        : c
    );

    setFormData({
      ...formData,
      contacts: updatedContacts,
    });

    setIsEditContactModalOpen(false);
    setEditingContact(null);
  };

  const handleContactClick = (contact, index) => {
    setContactName(contact.nombre || contact.name);
    setContactPhone(contact.telefono || contact.phone);
  };

  const headers = [
    { label: "Nombre", key: "nombre" },
    { label: "Localidad", key: "localidad" },
    { label: "Gremios", key: "gremios" },
    { label: "Estado", key: "estado" },
  ];

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer} ref={modalRef}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Operarios</h3>
          <button className={styles.closeButton} onClick={onClose}>
            <MyIcons name="close" width={24} height={24} color="black" />
          </button>
        </div>

        <FormSection title="Datos del Operario" style={{ marginBottom: "12px" }}>
          <div className={styles.sectionRow}>
            <Input
              ref={firstInputRef}
              label="Nombre"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Input
              label="Domicilio"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Dropdown
              label="Localidad"
              options={localityOptions}
              onSelect={handleLocalitySelect}
              selected={formData.locality}
              mainStyle={{ flex: 1 }}
              style={{ width: "100%" }}
            />
          </div>
          <div className={styles.sectionRow}>
            <Input
              label="Correo"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Input
              label="DNI"
              name="dni"
              value={formData.dni}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Input
              label="Observaciones"
              name="observations"
              value={formData.observations}
              onChange={handleInputChange}
              style={{ flex: 1 }}
            />
            <Dropdown
              label="Gremios"
              options={guildOptions}
              onSelect={handleGuildsSelect}
              selected={formData.guilds}
              mainStyle={{ flex: 1 }}
              style={{ width: "100%" }}
              multiSelect={true}
            />
          </div>
          
          <Checkbox
            label="Operario inactivo"
            checked={isInactive}
            onChange={(checked) => setIsInactive(checked)}
          />
        </FormSection>
          
        {/* Sección de Contactos */}
        <FormSection title="Contactos" style={{ marginBottom: "12px" }}>
            <div className={styles.sectionRow}>
              <Input
                label="Nombre"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="Nombre del contacto"
                style={{ flex: 1 }}
              />
              <Input
                label="Teléfono"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="Teléfono"
                style={{ flex: 1 }}
              />
            </div>
            
            {/* Lista de contactos */}
            {formData.contacts && formData.contacts.length > 0 && (
              <div className={styles.contactsList}>
                {formData.contacts.map((contact, index) => (
                  <ContactItem
                    key={index}
                    contact={{
                      id: index,
                      name: contact.nombre,
                      phone: contact.telefono,
                      address: "",
                    }}
                    onRemove={handleRemoveContact}
                    onClick={() => handleContactClick(contact, index)}
                    onEdit={() => handleEditContact(contact, index)}
                  />
                ))}
              </div>
            )}
            
            <Button 
              type="button" 
              variant="secondary"
              onClick={handleAddContact}
              style={{ marginTop: "24px" }}
            >
              AÑADIR CONTACTO
            </Button>
          </FormSection>
        
        <div style={{ marginTop: "12px", marginBottom: "24px", display: "flex", gap: "16px" }}>
          {!isEditMode ? (
            <>
              <Button 
                type="button" 
                variant="primary" 
                onClick={handleSaveOperator}
                disabled={!isFormValid()}
              >
                GUARDAR OPERARIO
              </Button>
            </>
          ) : (
            <>
              <Button 
                type="button" 
                variant="primary" 
                onClick={handleEditOperator}
                disabled={!isFormValid()}
              >
                EDITAR OPERARIO
              </Button>
              <Button 
                type="button" 
                variant="secondary" 
                onClick={handleCancelEdit}
              >
                CANCELAR
              </Button>
            </>
          )}
        </div>

        <div className={styles.tableSection}>
          <Input
            label="Buscar operario"
            placeholder="Buscar por nombre..."
            value={searchTerm}
            onChange={(e) => handleSearchOperators(e.target.value)}
            style={{ marginBottom: "16px" }}
          />
          <Table 
            headers={headers} 
            data={operators}
            onPress={handleOperatorRowClick}
          />
        </div>
      </div>

      {/* Modal de editar contacto */}
      <EditContactModal
        isOpen={isEditContactModalOpen}
        onClose={() => {
          setIsEditContactModalOpen(false);
          setEditingContact(null);
        }}
        contact={editingContact ? {
          ...editingContact,
          name: editingContact.nombre,
          phone: editingContact.telefono,
        } : null}
        onSave={handleSaveContact}
        rolOptions={[]}
        simpleMode={true}
      />
    </div>
  );
};

export default OperatorsModal;